import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { RtAnalyticsModule, TrackDirective } from '@recruitee/analytics';
import { collapse, CollapseState, trackByProp } from '@recruitee/common';

import { IconComponent } from '../../common/icon/icon.component';
import { IconName } from '../../icons-container/icons.type';
import { AnimState, MenuItem } from '../interfaces';
import { ItemType, SidebarItemComponent } from '../sidebar-item/sidebar-item.component';
import { SidebarInternalService } from '../sidebar/sidebar-internal.service';

@Component({
  selector: 'rt-sidebar-group',
  templateUrl: './sidebar-group.component.html',
  styleUrls: ['./sidebar-group.component.less'],
  animations: [collapse],
  imports: [
    IconComponent,
    RtAnalyticsModule,
    SidebarItemComponent,
    NgTemplateOutlet,
    TrackDirective,
  ],
})
export class SidebarGroupComponent implements OnInit {
  @Input() public heading: string;
  @Input() public items: MenuItem[] = [];
  @Input() public initialExpanded: boolean = true;
  @Input() public ItemType: typeof ItemType = ItemType;
  @Input() public isFirst: boolean = false;
  @Input() public isLast: boolean = false;
  @Input() public toggleWithSidebar: boolean = false;
  @Input() public icon: IconName;
  @Input() public rightSlot: TemplateRef<{ state: CollapseState }>;
  @Input() public walkthroughStepTemplate: TemplateRef<any>;

  @Output() public state: EventEmitter<AnimState> = new EventEmitter();

  public isSidebarMinimized: boolean = false;
  public isGroupMinimized: boolean;
  public trackByName = trackByProp<MenuItem>({ prop: 'name' });

  public get groupVisibilityState(): CollapseState {
    if (this.toggleWithSidebar) {
      return this.isGroupMinimized || this.isSidebarMinimized
        ? CollapseState.Collapsed
        : CollapseState.Normal;
    }

    return this.isGroupMinimized ? CollapseState.Collapsed : CollapseState.Normal;
  }

  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private cdRef: ChangeDetectorRef,
    private sidebarInternalService: SidebarInternalService,
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.initialExpanded?.currentValue) {
      this.isGroupMinimized = !this.initialExpanded;
    }
  }

  public ngOnInit(): void {
    this.sidebarInternalService.sidebarMinimized$
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(this.onSidebarMinimize.bind(this));
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public onHeaderClick(): void {
    if (this.toggleWithSidebar && this.isSidebarMinimized) {
      return;
    }

    this.toggleMinimized();
    this.state.emit({
      animState: this.isGroupMinimized ? CollapseState.Collapsed : CollapseState.Normal,
    });

    this.cdRef.detectChanges();
  }

  public toggleMinimized(value?: boolean): void {
    this.isGroupMinimized = value != null ? value : !this.isGroupMinimized;
  }

  private onSidebarMinimize(isMinimized: boolean): void {
    this.isSidebarMinimized = isMinimized;
    this.cdRef.detectChanges();
  }
}
